import { useCallback, useEffect, useState } from 'react'
import { Users, X, Eye, EyeOff, Loader2, SquarePen, Trash2, ToggleLeft, ToggleRight, Building2 } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { usePlatformFilterStore } from '../../../store/platformFilterStore'
import { platformUsersService } from '../../../services/platformUsersService'
import type { User } from '../../../types/api.types'
import { LoadErrorState } from '../../../components/LoadErrorState'
import { EmptyState } from '../../admin/components/EmptyState'
import { Tooltip } from '../../../components/Tooltip'

const ROLE_LABELS: Record<string, string> = { STAFF: 'Staff', MANAGER: 'Manager', SUPER_ADMIN: 'Super Admin' }
const ROLE_BADGE: Record<string, string> = {
  STAFF: 'bg-gray-100 text-gray-600',
  MANAGER: 'bg-blue-100 text-blue-700',
  SUPER_ADMIN: 'bg-purple-100 text-purple-700',
}

interface UserForm {
  name: string; email: string; phone: string; role: string; password: string
}
const EMPTY: UserForm = { name: '', email: '', phone: '', role: 'STAFF', password: '' }

export function PlatformUsersPage(): JSX.Element {
  const { organizations, selectedOrgId, selectOrg, allProperties } = usePlatformFilterStore()
  const orgProperties = allProperties.filter((p) => p.organization_id === selectedOrgId)

  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<UserForm>(EMPTY)
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])
  const [pwdVisible, setPwdVisible] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<Partial<UserForm>>({})

  const loadUsers = useCallback(async () => {
    if (!selectedOrgId) { setUsers([]); return }
    setLoading(true); setLoadError(false)
    try { setUsers(await platformUsersService.getByOrg(selectedOrgId)) }
    catch { setLoadError(true) }
    finally { setLoading(false) }
  }, [selectedOrgId])

  useEffect(() => { loadUsers() }, [loadUsers])

  function validate(): boolean {
    const e: Partial<UserForm> = {}
    if (!form.name.trim()) e.name = 'Required'
    if (form.email && !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Invalid email'
    if (!editingId && !form.password) e.password = 'Required for new users'
    if (form.password && (form.password.length < 8 || !/[a-zA-Z]/.test(form.password) || !/[0-9]/.test(form.password))) {
      e.password = 'Min 8 chars with letters and numbers'
    }
    setFormError(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit() {
    if (!validate() || !selectedOrgId) return
    const needsProps = form.role !== 'SUPER_ADMIN'
    setSubmitting(true)
    try {
      if (editingId) {
        await platformUsersService.update(selectedOrgId, editingId, {
          name: form.name.trim(), email: form.email || undefined,
          phone: form.phone || undefined, role: form.role,
          password: form.password || undefined,
          property_ids: needsProps ? selectedPropertyIds : undefined,
        })
        toast.success('User updated')
      } else {
        const newUser = await platformUsersService.create(selectedOrgId, {
          name: form.name.trim(), email: form.email || undefined,
          phone: form.phone || undefined,
          role: form.role as 'SUPER_ADMIN' | 'MANAGER' | 'STAFF',
          password: form.password,
          property_ids: needsProps ? selectedPropertyIds : undefined,
        })
        toast.success(
          newUser.email && form.password
            ? `User '${newUser.name}' created. Login credentials sent to ${newUser.email}.`
            : `User '${newUser.name}' created. No email sent — set a password to enable login.`
        )
      }
      setShowModal(false); setEditingId(null); setForm(EMPTY); setSelectedPropertyIds([])
      await loadUsers()
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Save failed')
    } finally { setSubmitting(false) }
  }

  async function handleToggle(user: User) {
    if (!selectedOrgId) return
    try {
      await platformUsersService.setStatus(selectedOrgId, user.id, !user.is_active)
      toast.success(user.is_active ? 'User deactivated' : 'User activated')
      await loadUsers()
    } catch { toast.error('Status update failed') }
  }

  async function handleDelete(user: User) {
    if (!selectedOrgId || !window.confirm(`Delete ${user.name}? This cannot be undone.`)) return
    try {
      await platformUsersService.delete(selectedOrgId, user.id)
      toast.success('User deleted')
      await loadUsers()
    } catch { toast.error('Delete failed') }
  }

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId)

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Users &amp; Roles</h1>
        <p className="mt-1 text-sm text-gray-500">Select an organisation from the top bar, then manage its users.</p>
      </div>

      {!selectedOrgId ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 py-20 text-center">
          <Building2 size={32} className="mb-3 text-gray-300" />
          <p className="text-[14px] font-medium text-gray-500">No organisation selected</p>
          <p className="mt-1 text-[13px] text-gray-400">Use the Organisation dropdown in the top bar to filter users.</p>
          {organizations.length > 0 && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {organizations.slice(0, 5).map((o) => (
                <button key={o.id} type="button" onClick={() => selectOrg(o.id)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] text-gray-600 hover:border-brand-green hover:text-brand-green">
                  {o.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-gray-500">
              Showing users for <span className="font-medium text-gray-900">{selectedOrg?.name ?? `Org #${selectedOrgId}`}</span>
            </p>
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); setShowModal(true) }}
              className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black hover:opacity-70">
              + Add User
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={22} className="animate-spin text-brand-green" />
              <span className="ml-2 text-sm text-gray-500">Loading users…</span>
            </div>
          ) : loadError ? (
            <LoadErrorState message="Couldn't load users" onRetry={loadUsers} />
          ) : users.length === 0 ? (
            <EmptyState icon={Users} message="No users in this organisation" />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Name</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Email</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Role</th>
                    <th className="px-5 py-3 text-left text-[12px] font-medium text-gray-500">Phone</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Status</th>
                    <th className="px-5 py-3 text-center text-[12px] font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50/40">
                      <td className="px-5 py-3.5 text-[13px] font-medium text-gray-900">{u.name}</td>
                      <td className="px-5 py-3.5 text-[13px] text-brand-green">{u.email || '—'}</td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${ROLE_BADGE[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                          {ROLE_LABELS[u.role] ?? u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-[13px] text-gray-600">{u.phone || '—'}</td>
                      <td className="px-5 py-3.5 text-center">
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-3">
                          <Tooltip label="Edit user">
                            <button type="button" onClick={() => {
                              setEditingId(u.id)
                              setForm({ name: u.name, email: u.email || '', phone: u.phone || '', role: u.role, password: '' })
                              setSelectedPropertyIds(u.property_ids ?? [])
                              setShowModal(true)
                            }} className="text-brand-green hover:text-brand-green/70"><SquarePen size={15} /></button>
                          </Tooltip>
                          <Tooltip label={u.is_active ? 'Deactivate' : 'Activate'}>
                            <button type="button" onClick={() => handleToggle(u)}
                              className={u.is_active ? 'text-brand-green hover:opacity-70' : 'text-gray-400 hover:text-gray-600'}>
                              {u.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                            </button>
                          </Tooltip>
                          <Tooltip label="Delete user">
                            <button type="button" onClick={() => handleDelete(u)} className="text-red-400 hover:text-red-600">
                              <Trash2 size={15} />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-[15px] font-bold text-gray-900">{editingId ? 'Edit User' : 'Add User'}</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              {([['Name*', 'name', 'text', 'Full name'], ['Email', 'email', 'email', 'user@example.com'], ['Phone', 'phone', 'tel', '+9230…']] as [string, keyof UserForm, string, string][]).map(([label, field, type, ph]) => (
                <div key={field}>
                  <label className="mb-1 block text-[13px] font-medium text-gray-700">{label}</label>
                  <input type={type} value={form[field]} onChange={(e) => { setForm({ ...form, [field]: e.target.value }); setFormError((p) => ({ ...p, [field]: undefined })) }}
                    className={`w-full rounded-lg border px-3 py-2.5 text-[14px] outline-none focus:border-brand-green ${formError[field] ? 'border-red-400' : 'border-gray-200'}`}
                    placeholder={ph} />
                  {formError[field] && <p className="mt-1 text-[12px] text-red-500">{formError[field]}</p>}
                </div>
              ))}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">Role*</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-green">
                  <option value="STAFF">Staff</option>
                  <option value="MANAGER">Manager</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </div>
              {form.role !== 'SUPER_ADMIN' && orgProperties.length > 0 && (
                <div>
                  <label className="mb-1 block text-[13px] font-medium text-gray-700">
                    Properties{form.role !== 'SUPER_ADMIN' ? '*' : ''} <span className="text-gray-400">(select at least one)</span>
                  </label>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-200 p-2 space-y-1">
                    {orgProperties.map((p) => (
                      <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50">
                        <input type="checkbox" checked={selectedPropertyIds.includes(p.id)}
                          onChange={(e) => setSelectedPropertyIds(e.target.checked
                            ? [...selectedPropertyIds, p.id]
                            : selectedPropertyIds.filter((id) => id !== p.id)
                          )}
                          className="h-3.5 w-3.5 accent-brand-green" />
                        <span className="text-[13px] text-gray-700">{p.name}</span>
                        <span className="ml-auto text-[11px] text-gray-400">{p.code}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="mb-1 block text-[13px] font-medium text-gray-700">
                  Password{editingId ? ' (leave blank to keep)' : '*'}
                </label>
                <div className="relative">
                  <input type={pwdVisible ? 'text' : 'password'} value={form.password}
                    onChange={(e) => { setForm({ ...form, password: e.target.value }); setFormError((p) => ({ ...p, password: undefined })) }}
                    className={`w-full rounded-lg border py-2.5 pl-3 pr-10 text-[14px] outline-none focus:border-brand-green ${formError.password ? 'border-red-400' : 'border-gray-200'}`}
                    placeholder={editingId ? 'Leave blank to keep' : '••••••••'} />
                  <button type="button" tabIndex={-1} onClick={() => setPwdVisible((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {pwdVisible ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {formError.password && <p className="mt-1 text-[12px] text-red-500">{formError.password}</p>}
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={handleSubmit} disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black hover:opacity-90 disabled:opacity-50">
                {submitting ? 'Saving…' : editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowModal(false)}
                className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
